<template>
  <div
    v-if="isMultiWindowEnabled"
    class="page-window-bar"
    role="tablist"
    aria-label="Open pages"
  >
    <button
      v-if="openWindows.length > 0"
      type="button"
      class="page-window-bar__nav-btn"
      :disabled="!canScrollLeft"
      title="Scroll tabs left"
      aria-label="Scroll tabs left"
      @click="scrollTabs(-1)"
    >
      <i class="bi bi-chevron-left" aria-hidden="true" />
    </button>

    <div
      ref="scrollContainer"
      class="page-window-bar__scroll"
      @scroll="updateScrollState"
    >
      <div
        v-if="openWindows.length === 0"
        class="page-window-tab page-window-tab--hint"
        aria-hidden="true"
      >
        <i class="bi bi-window-stack page-window-tab__icon" aria-hidden="true" />
        <span class="page-window-tab__label">Open a page from the sidebar — each page opens in its own window</span>
      </div>
      <div
        v-for="win in openWindows"
        :key="win.id"
        :ref="(el) => setTabRef(win.id, el)"
        class="page-window-tab"
        :class="{ 'page-window-tab--active': win.id === activeWindowId }"
        role="tab"
        :aria-selected="win.id === activeWindowId"
        :title="win.title"
        @click="onTabClick(win.id)"
        @auxclick="onTabAuxClick($event, win.id)"
      >
        <i :class="[getWindowIcon(win), 'page-window-tab__icon']" aria-hidden="true" />
        <span class="page-window-tab__label">{{ win.title }}</span>
        <button
          type="button"
          class="page-window-tab__close"
          :aria-label="`Close ${win.title}`"
          @click.stop="onClose(win.id)"
        >
          <i class="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>
    </div>

    <button
      v-if="openWindows.length > 0"
      type="button"
      class="page-window-bar__nav-btn"
      :disabled="!canScrollRight"
      title="Scroll tabs right"
      aria-label="Scroll tabs right"
      @click="scrollTabs(1)"
    >
      <i class="bi bi-chevron-right" aria-hidden="true" />
    </button>

    <button
      v-if="openWindows.length > 0"
      type="button"
      class="page-window-bar__nav-btn page-window-bar__refresh-btn"
      :disabled="!activeWindowId"
      title="Refresh this window — reload data after changes in other windows"
      aria-label="Refresh current window"
      @click="onRefresh"
    >
      <i class="bi bi-arrow-clockwise" aria-hidden="true" />
    </button>

    <button
      type="button"
      class="page-window-bar__switcher-btn"
      title="Window switcher"
      aria-label="Open window switcher"
      @click="$emit('open-switcher')"
    >
      <i class="bi bi-grid" aria-hidden="true" />
    </button>
  </div>
</template>

<script>
import { usePageWindows } from '@/composables/usePageWindows';
import { getIconForWindow } from '@/utils/pageWindowIcons';

export default {
  name: 'PageWindowBar',
  emits: ['open-switcher'],
  setup() {
    const {
      openWindows,
      activeWindowId,
      isMultiWindowEnabled,
      switchToWindow,
      closeWindow,
      refreshActiveWindow,
    } = usePageWindows();

    return {
      openWindows,
      activeWindowId,
      isMultiWindowEnabled,
      switchToWindow,
      closeWindow,
      refreshActiveWindow,
    };
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      tabRefs: {},
      scrollMaintenanceRaf: null,
      scrollStateRaf: null,
    };
  },
  watch: {
    openWindows: {
      handler() {
        this.scheduleScrollMaintenance();
      },
      deep: true,
    },
    activeWindowId() {
      this.scheduleScrollMaintenance();
    },
  },
  mounted() {
    this.scheduleScrollMaintenance();
    window.addEventListener('resize', this.updateScrollState);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateScrollState);
    if (this.scrollMaintenanceRaf) cancelAnimationFrame(this.scrollMaintenanceRaf);
    if (this.scrollStateRaf) cancelAnimationFrame(this.scrollStateRaf);
    this.tabRefs = {};
  },
  methods: {
    getWindowIcon(win) {
      return getIconForWindow(win);
    },
    setTabRef(id, el) {
      if (el) {
        this.tabRefs[id] = el;
      } else {
        delete this.tabRefs[id];
      }
    },
    getScrollContainer() {
      return this.$refs.scrollContainer;
    },
    scheduleScrollMaintenance() {
      if (this.scrollMaintenanceRaf) cancelAnimationFrame(this.scrollMaintenanceRaf);
      this.scrollMaintenanceRaf = requestAnimationFrame(() => {
        this.scrollMaintenanceRaf = null;
        this.$nextTick(() => {
          this.scrollActiveTabIntoView();
          this.updateScrollState();
        });
      });
    },
    updateScrollState() {
      if (this.scrollStateRaf) cancelAnimationFrame(this.scrollStateRaf);
      this.scrollStateRaf = requestAnimationFrame(() => {
        this.scrollStateRaf = null;
        const el = this.getScrollContainer();
        if (!el) {
          this.canScrollLeft = false;
          this.canScrollRight = false;
          return;
        }
        const maxScroll = el.scrollWidth - el.clientWidth;
        this.canScrollLeft = el.scrollLeft > 1;
        this.canScrollRight = el.scrollLeft < maxScroll - 1;
      });
    },
    scrollTabs(direction) {
      const el = this.getScrollContainer();
      if (!el) return;
      const amount = Math.max(160, el.clientWidth * 0.55);
      el.scrollBy({ left: direction * amount, behavior: 'smooth' });
      window.setTimeout(() => this.updateScrollState(), 280);
    },
    scrollActiveTabIntoView() {
      const container = this.getScrollContainer();
      if (!container || !this.activeWindowId) return;
      const tab = this.tabRefs[this.activeWindowId];
      if (!tab) return;

      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;
      const viewLeft = container.scrollLeft;
      const viewRight = viewLeft + container.clientWidth;

      if (tabLeft < viewLeft) {
        container.scrollTo({ left: tabLeft, behavior: 'smooth' });
      } else if (tabRight > viewRight) {
        container.scrollTo({ left: tabRight - container.clientWidth, behavior: 'smooth' });
      }
    },
    onTabClick(id) {
      this.switchToWindow(id, this.$router);
    },
    onClose(id) {
      this.closeWindow(id, this.$router);
    },
    onRefresh() {
      this.refreshActiveWindow(this.$router);
    },
    onTabAuxClick(event, id) {
      if (event.button === 1) {
        event.preventDefault();
        this.onClose(id);
      }
    },
  },
};
</script>

<style src="./styles/PageWindowBar.css"></style>
